import produce from 'immer';
import {
    ADD_DEPARTMENT,
    ADD_SEARCH_TERM,
    CLEAR_ALL_FILTERS,
    CLEAR_ALL_FILTERS_V2,
    CLEAR_FILTER_SECTION,
    CLEAR_FILTER_SECTION_V2,
    FETCH_FILTERS_FAILURE,
    FETCH_FILTERS_REQUEST,
    FETCH_FILTERS_SUCCESS,
    TOGGLE_FILTER,
    TOGGLE_FILTER_SELECTION,
    TOGGLE_INFO_CARD_VISIBILITY,
    TOGGLE_SORT_MODAL_VISIBILITY,
    UPDATE_DEPARTMENT_SELECTION,
    UPDATE_FILTER_SELECTION,
} from '../actions/constants';
import { SRP_TYPES, SRP_PROPERTY, DEFAULT_DEPARTMENT_ID } from '../constants';
import { togglePropertySelection } from '../helpers/togglePropertySelection';
import type {
    FilterGroupedList, FilterPairedList, FilterRange, SearchResultFilters, State,
} from '../types';

export const initialState: State = {
    data: undefined,
    filters: { active: {} },
    loading: false,
    error: null,
    // TODO: Remove this logic
    property: {
        activities: [],
        categories: [],
        multi_categories: [],
        departments: null,
        genders: [],
        brands: [],
        colors: [],
        sizes: [],
        price_min: null,
        price_max: null,
        materials: [],
    },
    searchKey: '',
    sortModalVisibility: false,
    infoCardVisibility: false,
    indexableResult: [],
};

const searchResultPage = produce((
    draft,
    action: SearchResultFilters,
) => {
    switch (action.type) {
        case ADD_SEARCH_TERM: {
            const hadIndexableResults = Object.keys(draft.indexableResult || []).length;

            if (!hadIndexableResults) {
                draft.searchKey = action.payload;
            }

            break;
        }

        // TODO: Remove this logic
        case UPDATE_DEPARTMENT_SELECTION: {
            const { id } = action.payload;

            const department = draft.data?.filters.find(({ type }) => type === SRP_TYPES.PAIRED_CHECKLIST) as FilterPairedList;

            if (department?.parents?.items) {
                const { items } = department.parents;

                items.forEach((item) => {
                    item.is_selected = (Number(item.id) === id) ? !item.is_selected : false;
                });
            }

            break;
        }

        // TODO: Remove this logic
        case UPDATE_FILTER_SELECTION: {
            const { filterProperty, id } = action.payload;

            const filterProperties = [
                SRP_PROPERTY.activities,
                SRP_PROPERTY.brands,
                SRP_PROPERTY.categories,
                SRP_PROPERTY.colors,
                SRP_PROPERTY.materials,
            ];

            if (filterProperties.includes(filterProperty)) {
                const filter = (draft.data?.filters as any)
                    .find(({ property }: any) => property === filterProperty);

                if (filter) {
                    filter.items = (filter.items as any)
                        .map((item: any) => (item.id === id ? { ...item, is_selected: !item.is_selected } : item));
                }
            }

            if (filterProperty === SRP_PROPERTY.genders) {
                const filter = (draft.data?.filters as unknown as FilterPairedList[])
                    .find(({ type }) => type === SRP_TYPES.PAIRED_CHECKLIST);

                if (filter) {
                    filter.children.items = filter.children.items
                        .map((item: any) => (item.id === id ? { ...item, is_selected: !item.is_selected } : item));
                }
            }

            if (filterProperty === SRP_PROPERTY.sizes) {
                const filter = (draft.data?.filters as unknown as FilterGroupedList[])
                    .find(({ property }) => property === filterProperty);

                if (filter) {
                    filter.groups = filter.groups.map(group => ({
                        ...group,
                        items: group.items.map((item: any) => (
                            item.id === id ? { ...item, is_selected: !item.is_selected } : item)),
                    }));
                }
            }

            break;
        }

        // TODO: Remove this logic
        case ADD_DEPARTMENT: {
            const { filterProperty, value, isDepartmentAll } = action.payload;

            if (value) {
                draft.property[filterProperty] = value;
            } else {
                draft.property[filterProperty] = isDepartmentAll ? DEFAULT_DEPARTMENT_ID : null;
            }

            draft.property.genders = [];

            break;
        }

        // TODO: Remove this logic
        case TOGGLE_FILTER: {
            const { filterProperty, value } = action.payload;

            if (filterProperty === SRP_TYPES.RANGE) {
                const [minValue, maxValue] = value;

                draft.property.price_min = minValue;
                draft.property.price_max = maxValue;

                break;
            }

            draft.property[filterProperty] = value;

            break;
        }

        case FETCH_FILTERS_FAILURE: {
            draft.loading = false;
            draft.error = action.payload;
            break;
        }

        case FETCH_FILTERS_REQUEST: {
            draft.loading = true;
            draft.error = null;
            break;
        }

        case FETCH_FILTERS_SUCCESS: {
            draft.loading = false;
            draft.data = action.payload;
            break;
        }

        case TOGGLE_FILTER_SELECTION: {
            const { filterProperty, id } = action.payload;

            const filterProperties = [
                SRP_PROPERTY.activities,
                SRP_PROPERTY.brands,
                SRP_PROPERTY.categories,
                SRP_PROPERTY.colors,
                SRP_PROPERTY.materials,
            ];

            if (filterProperties.includes(filterProperty)) {
                const filter = (draft.data?.filters as any)
                    .find(({ property }: any) => property === filterProperty);

                if (filter) {
                    filter.items = (filter.items as any)
                        .map((item: any) => (item.id === id ? { ...item, is_selected: !item.is_selected } : item));
                }
            }

            if (filterProperty === SRP_PROPERTY.departments) {
                const department = draft.data?.filters.find(({ type }) => (
                    type === SRP_TYPES.PAIRED_CHECKLIST
                )) as FilterPairedList;

                if (department.children?.items.length) {
                    delete draft.filters.active.genders;
                }

                if (department.parents.items) {
                    const { items } = department.parents;

                    items.forEach((item) => {
                        item.is_selected = (Number(item.id) === id) ? !item.is_selected : false;
                    });
                }
            }

            if (filterProperty === SRP_PROPERTY.genders) {
                const filter = (draft.data?.filters as FilterPairedList[])
                    .find(({ type }) => type === SRP_TYPES.PAIRED_CHECKLIST);

                if (filter?.children) {
                    filter.children.items = filter.children.items
                        .map((item: any) => (item.id === id ? { ...item, is_selected: !item.is_selected } : item));
                }
            }

            if (filterProperty === SRP_PROPERTY.sizes) {
                const filter = (draft.data?.filters as FilterGroupedList[])
                    .find(({ property }) => property === filterProperty);

                if (filter) {
                    filter.groups = filter.groups.map(group => ({
                        ...group,
                        items: group.items.map((item: any) => (
                            item.id === id ? { ...item, is_selected: !item.is_selected } : item)),
                    }));
                }
            }

            if (filterProperty === (SRP_PROPERTY.priceMax || SRP_PROPERTY.priceMin)) {
                const filter = (draft.data?.filters as FilterRange[]).find(({ type }) => type === SRP_TYPES.RANGE);

                if (filter) {
                    filter.has_range_selected = true;
                }
            }

            togglePropertySelection(draft.filters, filterProperty, id);

            break;
        }

        // TODO: Remove this logic
        case CLEAR_ALL_FILTERS:
            for (const key in draft.property) {
                if (Array.isArray(draft.property[key])) {
                    draft.property[key] = [];
                } else {
                    draft.property[key] = null;
                }
            }

            break;

        case CLEAR_ALL_FILTERS_V2:

            Object.keys(draft.filters.active).forEach((key) => {
                delete draft.filters.active[key];
            });

            break;

        // TODO: Remove this logic
        case CLEAR_FILTER_SECTION: {
            const filterKey = action.payload;

            if (filterKey === SRP_TYPES.RANGE) {
                draft.property.price_min = null;
                draft.property.price_max = null;

                break;
            }

            if (Array.isArray(draft.property[filterKey])) {
                draft.property[filterKey] = [];
            } else {
                draft.property[filterKey] = null;
            }

            break;
        }

        case CLEAR_FILTER_SECTION_V2: {
            const property = action.payload;

            if (Array.isArray(property)) {
                (action.payload as string[]).forEach((item: string) => delete draft.filters.active[item]);
            } else {
                delete draft.filters.active[property];
            }

            break;
        }

        case TOGGLE_INFO_CARD_VISIBILITY: {
            draft.infoCardVisibility = !draft.infoCardVisibility;
            break;
        }

        case TOGGLE_SORT_MODAL_VISIBILITY: {
            draft.sortModalVisibility = !draft.sortModalVisibility;
            break;
        }

        default:
            break;
    }
}, initialState);

export default searchResultPage;
